/**
 * Multi-Leg Pricing Policy — which origin a leg's regional rate is matched
 * against, stored on the contract as `originAttributionScope`.
 *
 * `leg` (the API and product default) matches every leg on its own region
 * pair. `serviceContinuityRun` holds the trip's originating region constant
 * across the run, so a return leg keeps the rate that carried the vehicle
 * out — which is what removes the need for "any-to-any" rate rules.
 */
export const ORIGIN_ATTRIBUTION_SCOPES = Object.freeze({
  PER_LEG: 'leg',
  TRIP_ORIGIN_REGION: 'serviceContinuityRun',
});

/** Section heading and dropdown label, per product. */
export const MULTI_LEG_PRICING_POLICY_LABEL = 'Multi-Leg Pricing Policy';

/** Existing contracts must be unchanged, so the form defaults to Per leg. */
export const DEFAULT_ORIGIN_ATTRIBUTION_SCOPE =
  ORIGIN_ATTRIBUTION_SCOPES.PER_LEG;

/**
 * Each option carries two tiers of copy, both provided by product:
 *
 * - `summary` — one clause, shown after the label in the dropdown's option
 *   rows and in the Contract Information card.
 * - `helperText` — the full behaviour including the fallback rule, shown as
 *   the dropdown's caption once that option is selected.
 */
export const ORIGIN_ATTRIBUTION_SCOPE_OPTIONS = [
  {
    value: ORIGIN_ATTRIBUTION_SCOPES.PER_LEG,
    label: 'Per leg',
    summary: 'Each leg prices on its own regional rate.',
    helperText:
      "Each leg uses the regional rate for its own pickup and drop-off. If no regional rate exists for that pair, the leg falls back to the trip's originating region rate.",
  },
  {
    value: ORIGIN_ATTRIBUTION_SCOPES.TRIP_ORIGIN_REGION,
    label: 'Trip origin region',
    summary: "All legs price off the trip's originating region.",
    helperText:
      "Every leg prices off the trip's originating region, not its own pickup. If a rate exists from the origin region to the leg's drop-off region it is used, otherwise the origin region rate applies.",
  },
];

/**
 * Resolves a stored scope to its dropdown option, falling back to the default
 * for a missing value or one this UI doesn't know about.
 * @param {string|null|undefined} scope
 * @returns {Object} The matching option
 */
export const getOriginAttributionScopeOption = (scope) =>
  ORIGIN_ATTRIBUTION_SCOPE_OPTIONS.find((opt) => opt.value === scope) ??
  ORIGIN_ATTRIBUTION_SCOPE_OPTIONS.find(
    (opt) => opt.value === DEFAULT_ORIGIN_ATTRIBUTION_SCOPE,
  );

/**
 * Normalizes an arbitrary value to a scope the API will accept.
 * @param {string|null|undefined} scope
 * @returns {string} `'leg'` or `'serviceContinuityRun'`
 */
export const normalizeOriginAttributionScope = (scope) =>
  Object.values(ORIGIN_ATTRIBUTION_SCOPES).includes(scope)
    ? scope
    : DEFAULT_ORIGIN_ATTRIBUTION_SCOPE;

/** Human label for a stored scope — used by the Contract Information card. */
export const getOriginAttributionScopeLabel = (scope) =>
  getOriginAttributionScopeOption(scope).label;
